import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import type {
    Vehicle,
    VehicleDerivative,
    AdditionalVehicleDetailsFormInputs,
} from '@/features/vehiclesearch';
import { isVehicle } from '@features/vehiclesearch';
import { updateExistingVehicle, convertDerivativeToVehicle } from '../utils/vehicleDetailsMapping';
import { setSelectedVehicle } from '../store/quoteSlice';

export const useVehicleDetailsUpdate = (
    selectedVehicle: Vehicle | VehicleDerivative | null,
): ((data: AdditionalVehicleDetailsFormInputs) => Promise<void>) => {
    const dispatch = useDispatch();

    const validateVehicleData = useCallback((vehicle: Vehicle): boolean => {
        return !!(
            vehicle.type &&
            vehicle.full_description &&
            vehicle.derivative &&
            vehicle.current_status &&
            vehicle.date_of_first_registration
        );
    }, []);

    const updateVehicleWithDetails = useCallback(
        async (data: AdditionalVehicleDetailsFormInputs): Promise<void> => {
            if (!selectedVehicle) {
                throw new Error('No vehicle selected');
            }

            try {
                let updatedVehicle: Vehicle;

                if (isVehicle(selectedVehicle)) {
                    updatedVehicle = updateExistingVehicle(selectedVehicle, data);
                } else {
                    updatedVehicle = convertDerivativeToVehicle(selectedVehicle, data);
                }

                if (!validateVehicleData(updatedVehicle)) {
                    throw new Error('Invalid vehicle data after update');
                }

                dispatch(setSelectedVehicle(updatedVehicle));
            } catch (error) {
                console.error('Failed to update vehicle:', error);
                throw error; // Re-throw so QuoteWizard can handle it
            }
        },
        [selectedVehicle, validateVehicleData, dispatch],
    );

    return updateVehicleWithDetails;
};
